'use client';

import { useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { ApiClientError } from '@/shared/lib/api';
import { cn } from '@/shared/lib/cn';
import { ProjectGoalsSheet } from '@/features/project/ui/ProjectGoalsSheet';
import { useProject } from '@/features/project/hooks/use-projects';
import { useEstimateDetail, useEstimateActions } from '../hooks/use-estimate';
import { useConceptSelectedRenders } from '../hooks/use-concept-selected-renders';
import type { DesignLineContext } from '../api/design-line-service';
import { useEstimateGoalsSheet } from '../hooks/use-estimate-goals-sheet';
import { useEstimateItems } from '../hooks/use-estimate-items';
import { useDebouncedCommit } from '../hooks/use-debounced-commit';
import {
  EstimateSettingsList,
  toEstimateSettingsValues,
} from './EstimateHeader';
import { EstimateTitleTags } from './EstimateTitleTags';
import { EstimateCurrencySheet } from './EstimateCurrencySheet';
import { EstimateSection } from './EstimateSection';
import { EstimateGeneratingState } from './EstimateGeneratingState';
import { EstimateTotals } from './EstimateTotals';
import {
  COST_ESTIMATE_OVERWRITE_REQUIRED,
  type EstimateSection as Section,
  type UpdateEstimateItemRequest,
  type UpdateEstimateRequest,
} from '../api/estimate-service';
import { useAssistantAction } from '@/shared/assistant';

interface EstimateEditorProps {
  conceptId: string;
  projectId: string;
  estimateId: string;
  /** The design's line context — banners and the line's update state. */
  lineContext: DesignLineContext;
  locale: string;
  /** Two-column layout: the settings list + staleness banner + regenerate CTA
   *  live in the inspector aside (EstimateSettingsPanel), so the editor body
   *  must not render its inline copies. */
  settingsHostedExternally?: boolean;
}

const FIELD_DEBOUNCE_MS = 600;
const HEADER_KEY = 'header';

/**
 * Single-estimate editor. Renders the header + Materials/Labor sections when
 * the estimate is `ready`; otherwise a pending/generating spinner or a failed
 * banner with retry. Header and item edits are debounced PATCHes; item
 * mutations trigger a server-side total recompute that arrives over WS, so the
 * detail query is invalidated after every item mutation.
 */
export function EstimateEditor({
  conceptId,
  projectId,
  estimateId,
  lineContext,
  locale,
  settingsHostedExternally = false,
}: EstimateEditorProps) {
  const t = useTranslations('common.estimate.editor');
  const tLine = useTranslations('common.designLine');
  const tProject = useTranslations('project');

  // A building that does not exist yet: the design was reconstructed from
  // drawings, so every quantity below is derived from a model, not measured.
  // Server-computed flag — never derived client-side from the stage.
  const { data: projectResponse } = useProject(projectId);
  const isReconstruction =
    projectResponse?.data?.attributes?.is_reconstruction === true;

  const { data: estimate, isLoading } = useEstimateDetail(estimateId);
  const {
    createEstimate: createLineEstimate,
    isCreating: isUpdatingLine,
    regenerateEstimate,
    isRegenerating,
    updateEstimate,
    changeCurrency,
    isChangingCurrency,
    changeMeasurementSystem,
    isChangingMeasurement,
    changeLanguage,
    isChangingLanguage,
  } = useEstimateActions(projectId, conceptId);
  const {
    createItem,
    updateItem,
    applyItemDraft,
    deleteItem,
    reorderItems,
    lastAddedItemId,
    clearAddedItemHighlight,
  } = useEstimateItems(conceptId, estimateId);

  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  // Design-line banners (docs/estimate/UI-SPEC-design-lines.md). "Update for
  // this design" orders the line's next version from this design's renders;
  // "New estimate" starts a line for the design the shown version was
  // calculated for. "Keep" hides the different-design notice for that version
  // only, locally.
  const selectedRenders = useConceptSelectedRenders(conceptId);
  const {
    createEstimate: createVersionEstimate,
    isCreating: isCreatingNewLine,
  } = useEstimateActions(projectId, estimate?.conceptId || conceptId);
  const [keptVersionId, setKeptVersionId] = useState<string | null>(null);

  // Independent currency-picker dialog — opened by the currency tag next to
  // the title. Deliberately NOT one of the settings NestedSheet drill-downs:
  // the currency switch re-denominates the visible estimate immediately,
  // while the settings sheets hold generation inputs.
  const [showCurrencySheet, setShowCurrencySheet] = useState(false);

  // The region/facade-area rows open the shared project-goals sheet
  // (NestedSheet) so the user stays on the Estimate tab — Apple/Android sheet
  // parity, no page navigation (close → flush + estimate re-fetch — see the
  // hook).
  const { showGoalsSheet, handleGoalsSheetChange } = useEstimateGoalsSheet(
    projectId,
    estimateId
  );

  // Accumulate header field edits per debounce window so multiple fast field
  // changes flush as one PATCH with the latest values.
  const headerDraftRef = useRef<UpdateEstimateRequest>({});

  const headerCommit = useDebouncedCommit<UpdateEstimateRequest>(
    (_key, value) => {
      if (Object.keys(value).length === 0) return;
      updateEstimate(estimateId, value);
      headerDraftRef.current = {};
    },
    FIELD_DEBOUNCE_MS
  );

  // Per-item debounced PATCH — each item id has its own pending timer so a
  // stale edit on one row can't clobber another (debounce + clear race).
  const itemDraftsRef = useRef<Map<string, UpdateEstimateItemRequest>>(
    new Map()
  );
  const itemCommit = useDebouncedCommit<UpdateEstimateItemRequest>(
    (itemId, value) => {
      if (Object.keys(value).length === 0) return;
      updateItem({ itemId, data: value });
      itemDraftsRef.current.delete(itemId);
    },
    FIELD_DEBOUNCE_MS
  );

  const handleHeaderFieldChange = (data: UpdateEstimateRequest) => {
    const merged = { ...headerDraftRef.current, ...data };
    headerDraftRef.current = merged;
    headerCommit.schedule(HEADER_KEY, merged);
  };

  // Latest display currency / measurement system for stamping item patches at
  // scheduling time — refs so the stable debounced-commit callbacks never read
  // a stale closure.
  const currencyRef = useRef(estimate?.currency);
  currencyRef.current = estimate?.currency;
  const measurementRef = useRef(estimate?.measurement_system);
  measurementRef.current = estimate?.measurement_system;

  const handleItemFieldChange = (
    itemId: string,
    data: UpdateEstimateItemRequest
  ) => {
    // Instant cache write so the row total AND the totals summary recompute on
    // this very keystroke — the debounced PATCH below reconciles with the
    // server later.
    applyItemDraft(itemId, data);
    const merged = { ...(itemDraftsRef.current.get(itemId) ?? {}), ...data };
    // Stamp the display currency the price was typed in (stale-device guard):
    // the server 422s when the echo mismatches the estimate's current currency,
    // so a price entered on a device that missed a currency switch can't land
    // as a wrongly-denominated amount.
    if (merged.unit_price !== undefined && currencyRef.current) {
      merged.currency = currencyRef.current;
    }
    // Same guard for the measurement system: quantity/unit/unit_price are all
    // denominated in the display system the user is looking at.
    if (
      (merged.quantity !== undefined ||
        merged.unit !== undefined ||
        merged.unit_price !== undefined) &&
      measurementRef.current
    ) {
      merged.measurement_system = measurementRef.current;
    }
    itemDraftsRef.current.set(itemId, merged);
    itemCommit.schedule(itemId, merged);
  };

  const { materialsItems, laborItems } = useMemo(() => {
    // Re-sort by sort_order: the fetch is pre-sorted, but the optimistic
    // reorder re-stamps sort_order in the cache without re-shuffling the array.
    const items = [...(estimate?.items ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order
    );
    return {
      materialsItems: items.filter((i) => i.section === 'materials'),
      laborItems: items.filter((i) => i.section === 'labor'),
    };
  }, [estimate?.items]);

  const handleAddItem = (section: Section) => {
    const siblings = section === 'materials' ? materialsItems : laborItems;
    const maxSort = siblings.reduce(
      (max, i) => Math.max(max, i.sort_order),
      -1
    );
    createItem({
      section,
      name: t('newItemName'),
      quantity: 1,
      unit_price: 0,
      sort_order: maxSort + 1,
      // Currency + measurement-system echoes (stale-device guards) — see
      // handleItemFieldChange.
      ...(currencyRef.current ? { currency: currencyRef.current } : {}),
      ...(measurementRef.current
        ? { measurement_system: measurementRef.current }
        : {}),
    });
  };

  const runRegenerate = async (confirmOverwrite?: boolean) => {
    try {
      await regenerateEstimate({ estimateId, confirmOverwrite });
      setShowOverwriteConfirm(false);
    } catch (error) {
      // Edited estimate → server demands explicit confirmation. Open the
      // confirm dialog; the retry sends confirm_overwrite=true.
      if (
        error instanceof ApiClientError &&
        error.code === COST_ESTIMATE_OVERWRITE_REQUIRED
      ) {
        setShowOverwriteConfirm(true);
        return;
      }
      // Other errors already surfaced as a toast in the mutation onError.
    }
  };
  // The AI architect's "Redo estimate" card presses this same button.
  useAssistantAction('regenerate_estimate', estimateId, () => runRegenerate());

  if (isLoading || !estimate) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Pending / generating — generation is in flight. A narrated calculation
  // stepper stands in for a bare spinner (see EstimateGeneratingState).
  // A new version of the line is being calculated: the shown version stays
  // the last finished one server-side, so the tab narrates the update.
  if (
    estimate.status === 'pending' ||
    estimate.status === 'generating' ||
    lineContext.line?.is_updating
  ) {
    return <EstimateGeneratingState />;
  }

  // Failed — surface the server-owned reason and offer a retry. Same
  // readable cap as the generating state above.
  if (estimate.status === 'failed') {
    return (
      <div className="mx-auto w-full max-w-[560px] space-y-4">
        <NoticeBanner
          variant="error"
          title={t('failedTitle')}
          message={estimate.failure_reason ?? undefined}
        />
        <div className="flex justify-center">
          <Button
            variant="teal"
            size="cta"
            onClick={() => runRegenerate()}
            disabled={isRegenerating}
          >
            {isRegenerating && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t('retryGeneration')}
          </Button>
        </div>
      </div>
    );
  }

  // Ready — full editor. A currency or measurement-system switch rewrites
  // every amount server-side, so the whole editor is locked while in flight.
  const isBusy =
    isRegenerating ||
    isChangingCurrency ||
    isChangingMeasurement ||
    isChangingLanguage;
  // A past version of the design line: history, every write is refused (409).
  const isReadOnly = estimate.is_read_only;
  const lineId = lineContext.design_line_id;
  return (
    <>
      <div className={cn('flex flex-col gap-3 pb-2')}>
        {/* Priced for another design of the building — offer to update the
            line for this one. Copy is the API `status_text`. */}
        {lineId && !lineContext.is_priced_for_this_design && (
          <NoticeBanner
            variant="info"
            message={lineContext.status_text}
            action={
              <Button
                variant="ghost"
                size="sm"
                disabled={isUpdatingLine}
                onClick={() =>
                  createLineEstimate({
                    selected_renders: selectedRenders,
                    language: estimate.language,
                    design_line_id: lineId,
                  })
                }
              >
                {tLine('updateEstimateForDesign')}
              </Button>
            }
          />
        )}

        {/* The update rewrote most lines — likely a different design. */}
        {estimate.looks_like_different_design &&
          keptVersionId !== estimate.id && (
            <NoticeBanner
              variant="warning"
              message={estimate.different_design_notice ?? undefined}
              action={
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setKeptVersionId(estimate.id)}
                  >
                    {tLine('keep')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isCreatingNewLine}
                    onClick={() =>
                      createVersionEstimate({
                        selected_renders: estimate.source_render_ids,
                        language: estimate.language,
                        new_design_line: true,
                      })
                    }
                  >
                    {tLine('newEstimate')}
                  </Button>
                </div>
              }
            />
          )}

        {/* Export lives in the concept page "⋯" menu ("Export estimate"
            submenu in ConceptActions) — no inline export affordance here. */}

        {/* The estimate is where a reconstruction turns into money, so the
            marker leads the document instead of riding quietly on an image:
            these quantities come from a building assembled out of drawings and
            a brief, not from a survey of something that exists. Warning (not
            info) on purpose — the reader is about to price it. */}
        {isReconstruction && (
          <NoticeBanner
            variant="warning"
            title={tProject('reconstructionMarker')}
            message={tProject('reconstructionEstimateNotice')}
          />
        )}

        {/* Totals summary card LEADS the estimate. It carries the page heading
            ("Cost estimate") + the display-denomination tag-toggles (currency
            picker / instant units toggle) as its own header — the facade area
            rides there as a subtitle — so the title and the headline numbers
            read as one block. The tags render in BOTH layouts (they belong to
            the document, not the settings inspector aside). */}
        <EstimateTotals
          estimate={estimate}
          titleTags={
            <EstimateTitleTags
              currency={estimate.currency}
              measurementSystem={estimate.measurement_system}
              disabled={isBusy || isReadOnly}
              onOpenCurrencyPicker={() => setShowCurrencySheet(true)}
              isChangingCurrency={isChangingCurrency}
              onMeasurementToggle={() =>
                changeMeasurementSystem(
                  estimateId,
                  estimate.measurement_system === 'metric'
                    ? 'imperial'
                    : 'metric'
                )
              }
              isChangingMeasurement={isChangingMeasurement}
            />
          }
        />

        <EstimateSection
          section="materials"
          items={materialsItems}
          notes={estimate.materials_notes}
          unitOptions={estimate.unit_options ?? []}
          disabled={isBusy}
          readOnly={isReadOnly}
          onFieldChange={handleItemFieldChange}
          onFieldCommit={(itemId) => itemCommit.flush(itemId)}
          onNotesChange={(value) =>
            handleHeaderFieldChange({ materials_notes: value })
          }
          onNotesCommit={() => headerCommit.flush(HEADER_KEY)}
          onDeleteItem={deleteItem}
          onAddItem={handleAddItem}
          onReorderItems={(section, itemIds) =>
            reorderItems({ section, itemIds })
          }
          lastAddedItemId={lastAddedItemId}
          onAddedItemShown={clearAddedItemHighlight}
        />

        <EstimateSection
          section="labor"
          items={laborItems}
          notes={estimate.labor_notes}
          unitOptions={estimate.unit_options ?? []}
          disabled={isBusy}
          readOnly={isReadOnly}
          onFieldChange={handleItemFieldChange}
          onFieldCommit={(itemId) => itemCommit.flush(itemId)}
          onNotesChange={(value) =>
            handleHeaderFieldChange({ labor_notes: value })
          }
          onNotesCommit={() => headerCommit.flush(HEADER_KEY)}
          onDeleteItem={deleteItem}
          onAddItem={handleAddItem}
          onReorderItems={(section, itemIds) =>
            reorderItems({ section, itemIds })
          }
          lastAddedItemId={lastAddedItemId}
          onAddedItemShown={clearAddedItemHighlight}
        />

        {/* Editing affordances are easy to miss in a dense table — spell them
            out once at the end of the estimate. */}
        {!isReadOnly && (
          <NoticeBanner variant="info" message={t('editHintBanner')} />
        )}

        {/* Stacked (single-column): the settings list sits AFTER the estimate,
            right next to the regenerate CTA — the staleness banner rides above
            the group (not tied to a single setting). Two-column hosts all of
            these in the inspector aside (EstimateSettingsPanel) — rendering
            them here too would double them. */}
        {!settingsHostedExternally && !isReadOnly && (
          // These are SETTINGS, not the estimate document — capped to the
          // readable form width on wide single-column screens (centered) so they
          // read as narrow settings below the full-width materials/labor table
          // above. Mirrors the Views/Blueprint forms. Apple/Android parity.
          <div className="mx-auto flex w-full max-w-[560px] flex-col gap-3">
            {/* Server-computed staleness flag (`has_newer_renders`): the
                concept gained completed renders after this estimate was
                calculated, so the numbers may not reflect the latest
                designs. */}
            {estimate.has_newer_renders && (
              <NoticeBanner
                variant="warning"
                message={t('newerRendersWarning')}
              />
            )}
            <EstimateSettingsList
              values={toEstimateSettingsValues(estimate)}
              disabled={isBusy}
              onLanguageChange={(language) =>
                changeLanguage(estimateId, language)
              }
              isChangingLanguage={isChangingLanguage}
              onOpenProjectGoals={() => handleGoalsSheetChange(true)}
              onSpecialRequirementsSave={(value) =>
                updateEstimate(estimateId, { special_requirements: value })
              }
            />
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="cta"
                onClick={() => runRegenerate()}
                disabled={isRegenerating}
              >
                {isRegenerating && (
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                )}
                {t('regenerate')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Independent currency-picker dialog (modal, not a NestedSheet
          drill-down) — opened by the currency tag. */}
      <EstimateCurrencySheet
        open={showCurrencySheet}
        onOpenChange={setShowCurrencySheet}
        value={estimate.currency}
        locale={locale}
        onSelect={(currency) => changeCurrency(estimateId, currency)}
      />

      {/* Shared project-goals editor (goals + region + facade area) — opened
          by the facade-area link in the settings list (stacked layout only).
          On two-column the settings list AND this sheet live in the inspector
          aside (EstimateSettingsPanel) — rendering it here too would mount a
          SECOND, never-opened NestedSheet in the content column. That column's
          NestedSheetStackProvider has no registered portalContainer, so the
          closed sheet renders in place as `absolute inset-0 translate-x-full`
          and, at certain widths, leaks a sliver of itself (its own scrollbar +
          panel edge) into the gap between the content column and the aside. */}
      {!settingsHostedExternally && (
        <ProjectGoalsSheet
          projectId={projectId}
          open={showGoalsSheet}
          onOpenChange={handleGoalsSheetChange}
        />
      )}

      {/* Overwrite confirm — only shown when the estimate was hand-edited and
          the server requested explicit confirmation (409). */}
      <AlertDialog
        open={showOverwriteConfirm}
        onOpenChange={setShowOverwriteConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('overwriteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('overwriteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRegenerating}>
              {t('overwriteCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runRegenerate(true)}
              disabled={isRegenerating}
            >
              {t('overwriteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
